/**
 * OverUnderConfigSection - Configuration UI for OVER_UNDER question types
 *
 * This component renders the Stat Threshold field for Over/Under questions.
 *
 * Note: Player/Team selection is handled separately in QuestionFieldsSection
 * Note: Stat Type is handled separately in StatTypeSelectionSection
 *
 * Renders when question_type === 'player_over_under' or 'team_over_under' (or legacy 'over_under')
 */

import { FormikProps } from 'formik';
import { QuestionFormValues } from '@/utils/forms/questionTransformers';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

interface OverUnderConfigSectionProps {
  formik: FormikProps<QuestionFormValues>;
  isEditable: boolean;
}

export function OverUnderConfigSection({ formik, isEditable }: OverUnderConfigSectionProps) {
  const { values, setFieldValue } = formik;

  const config = values.question_metadata?.grading?.config as
    | {
        threshold?: number;
      }
    | undefined;

  const threshold = config?.threshold;

  const isPlayerOverUnder = values.question_type === ('player_over_under' as any);
  const isTeamOverUnder = values.question_type === ('team_over_under' as any);
  const isLegacyOverUnder = values.question_type === 'over_under';

  // Only render for over/under question types
  if (!isPlayerOverUnder && !isTeamOverUnder && !isLegacyOverUnder) {
    return null;
  }

  const handleThresholdChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value === '' ? undefined : parseFloat(e.target.value);
    setFieldValue('question_metadata.grading.config.threshold', value);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Over/Under Configuration</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        {/* Stat Threshold */}
        <div className="flex flex-col gap-2">
          <Label htmlFor="threshold">
            Stat Threshold <span className="text-destructive">*</span>
          </Label>
          <Input
            id="threshold"
            type="number"
            step="0.5"
            value={threshold ?? ''}
            onChange={handleThresholdChange}
            disabled={!isEditable}
            placeholder="e.g., 72.5"
          />
          <p className="text-sm text-muted-foreground">
            The threshold value for the stat (e.g., 72.5)
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
